import { NgClass, NgStyle } from '@angular/common';
import { Component, input, output } from '@angular/core';
import { IonCard, IonChip, IonIcon, IonLabel, IonText } from '@ionic/angular/standalone';
import { Entity, Enums } from '@libs/mtjr/shared/generated/types';
import { TranslatePipe } from '@ngx-translate/core';

export interface ProductAttribute {
  key: string;
  label: string;
  type: Enums.AttributeType;
  values: Entity.VariationAttribute[];
}

@Component({
  selector: 'mtjr-product-attributes-section',
  standalone: true,
  imports: [NgClass, NgStyle, IonLabel, IonCard, IonText, IonIcon, IonChip, TranslatePipe],
  template: `
    @if (attributes().length > 0) {
      <div class="mx-2 mt-2">
        @for (attr of attributes(); track $index) {
          <div class="px-1 py-1">
            <div class="mt-3">
              <ion-label class="mx-3">{{ 'ATTRIBUTE.' + attr.label.toUpperCase() | translate }}</ion-label>
            </div>
            <div class="mx-1 mt-2 flex flex-wrap gap-1" [ngClass]="{ 'justify-center': isVoucherVendor() }">
              @for (attrValue of attr.values; track $index) {
                @if (attr.type !== 'color') {
                  @if (isVoucherVendor()) {
                    <ion-card
                      class="!m-0 h-full min-w-[110px] cursor-pointer"
                      [ngStyle]="{
                        background: availableAttributes().includes(attr.key + '-' + attrValue.key)
                          ? productBgColor()
                          : 'gray',
                      }"
                      [ngClass]="{
                        'text-primary border-white': selectedAttributes().includes(attrValue),
                      }"
                      (click)="onAttributeToggle(attrValue)"
                    >
                      <div class="relative">
                        <div
                          class="absolute -end-10 top-0 h-20 w-20 bg-white/5 [border-radius:60%_40%_30%_70%_/_50%_60%_30%_80%]"
                        ></div>
                        <div
                          class="absolute -end-14 top-0 h-20 w-20 bg-white/5 [border-radius:80%_40%_50%_70%_/_50%_60%_40%_60%]"
                        ></div>
                      </div>

                      <div class="hover:bg-light/10 flex h-full min-h-14 items-center justify-center px-2 py-1.5">
                        <ion-text color="dark" class="me-2 line-clamp-1 select-none text-lg text-white">
                          {{ attrValue.label }} {{ 'COMMON.CURRENCY_SYMBOL' | translate }}
                        </ion-text>
                        @if (selectedAttributes().includes(attrValue)) {
                          <ion-icon name="checkmark-circle-outline" class="text-2xl text-white" />
                        }
                      </div>
                    </ion-card>
                  } @else {
                    <ion-chip
                      outline="true"
                      class="flex min-w-10 items-center justify-center text-center"
                      [ngClass]="{
                        'border-primary text-primary bg-primary/10': selectedAttributes().includes(attrValue),
                      }"
                      (click)="onAttributeToggle(attrValue)"
                    >
                      <ion-label>{{ attrValue.label }}</ion-label>
                      @if (selectedAttributes().includes(attrValue)) {
                        <ion-icon name="checkmark" color="success" size="small"></ion-icon>
                      }
                    </ion-chip>
                  }
                } @else {
                  <div
                    (click)="onAttributeToggle(attrValue)"
                    class="bg-medium/15 border-medium flex cursor-pointer items-center justify-center overflow-hidden rounded-full border pe-3"
                    [ngClass]="{
                      'border-primary border-solid': selectedAttributes().includes(attrValue),
                    }"
                  >
                    <div
                      fill="clear"
                      class="!m-0 flex size-8 items-center justify-center rounded-full !p-0 text-white"
                      [ngStyle]="{
                        'background-color': attrValue.key,
                      }"
                      [ngClass]="{
                        'border-medium text-primary border-spacing-1 border-2 border-solid':
                          selectedAttributes().includes(attrValue),
                      }"
                    ></div>
                    <ion-label class="mx-2">{{ 'COLOR.' + attrValue.label.toUpperCase() | translate }}</ion-label>
                    @if (selectedAttributes().includes(attrValue)) {
                      <ion-icon slot="icon-only" name="checkmark" color="success" class="text-xl"></ion-icon>
                    }
                  </div>
                }
              }
            </div>
          </div>
        }
      </div>
    }
  `,
})
export class VxProductAttributesSectionComponent {
  attributes = input.required<ProductAttribute[]>();
  selectedAttributes = input.required<Entity.VariationAttribute[]>();
  availableAttributes = input.required<string[]>();
  isVoucherVendor = input<boolean>(false);
  productBgColor = input<string | undefined | null>('#000000');

  attributeToggle = output<Entity.VariationAttribute>();

  onAttributeToggle(attrValue: Entity.VariationAttribute) {
    this.attributeToggle.emit(attrValue);
  }
}
